import { holidayPseudoResourceType } from '@lmt/unicorn-shared/app/components/absences/common/types';
import { CheckBox } from '@lmt/unicorn-shared/app/components/common/ui-kit/CheckBox';
import { IconButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/IconButton';
import { OptionList } from '@lmt/unicorn-shared/app/components/common/ui-kit/inputs/search/OptionList';
import { RadioButton } from '@lmt/unicorn-shared/app/components/common/ui-kit/RadioButton';
import CrossIcon from '@lmt/unicorn-shared/app/icons/cross.svg?react';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useEventCallback } from '@mui/material';
import { snakeCase } from 'lodash';
import { ResourceSettings, SelectedResources } from '../visit/common/types';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';

import styles from './ResourcePanel.module.scss';
import AddContent from '@lmt/unicorn-shared/app/icons/add-content.svg?react';
import clsx from 'clsx';
import { sortResourceKeys } from './resources-helper';
import {
  SchedulerResourceGroups,
  SchedulerResourceToPick,
} from './scheduling-assistant-types';
import { sortResourceValues } from '@lmt/unicorn-shared/app/helpers/sort';

interface ResourcePanelProps {
  shownResources: SchedulerResourceGroups;
  pickResource: (
    resourceType: string,
    resource: SchedulerResourceToPick,
  ) => void;
  removeResource: (resourceType: string, resourceId: number) => void;
  pickAllResources: (resourceType: string) => void;
  removeAllResources: (resourceType: string) => void;
  resourceGroups: SchedulerResourceGroups;
  resourceSettings: ResourceSettings;
  selectedResources: SelectedResources;
  setResourceSelected: (
    type: string,
    resourceId: number,
    selected: boolean,
  ) => void;
  anyAvailableResourceEnabled?: boolean; // if true, there is a special option 'Any available' in resource list
  typesWithAnyAvailableResource: string[];
  addTypeWithAnyAvailableResource: (type: string) => void;
  removeTypeWithAnyAvailableResource: (type: string) => void;
}

export const ResourcePanel: FC<ResourcePanelProps> = React.memo(
  function ResourcePanel(props) {
    const i18nFilter = useScopedTranslation('uikit.filter');
    const i18n = useScopedTranslation('event.visit');
    const {
      shownResources,
      pickResource,
      pickAllResources,
      removeAllResources,
      removeResource,
      resourceGroups,
      resourceSettings,
      selectedResources,
      setResourceSelected,
      anyAvailableResourceEnabled,
      typesWithAnyAvailableResource,
      addTypeWithAnyAvailableResource,
      removeTypeWithAnyAvailableResource,
    } = props;

    useEffect(
      function selectResourceIfNoneIsSelected() {
        Object.keys(shownResources).forEach((type) => {
          if (!resourceSettings[type]?.isMultiSelect) {
            const shownResourcesForType = (shownResources[type] || []).filter(
              (shownResource) =>
                resourceGroups[type]?.find(
                  (resource) => resource.id === shownResource.id,
                ),
            );
            if (shownResourcesForType.length > 0) {
              const selectedResourcesForType = selectedResources[type] || [];
              if (selectedResourcesForType.length === 0) {
                setResourceSelected(type, shownResourcesForType[0].id, true);
              }
            }
          }
        });
      },
      [selectedResources],
    );

    const [resourceTypeToAddFor, setResourceTypeToAddFor] = useState<
      string | undefined
    >();
    const [resourceTypeToAdd_Element, setResourceTypeToAdd_Element] =
      useState<Element | null>(null);
    const onPopupClose = useCallback(() => {
      setResourceTypeToAddFor(undefined);
      setResourceTypeToAdd_Element(null);
    }, []);

    const isResourceAlreadyShown = useCallback(
      (resource: SchedulerResourceToPick, resourceType: string) => {
        return shownResources[resourceType]
          .map((x) => x.id)
          .includes(resource.id);
      },
      [shownResources],
    );

    /**
     * Remove 'Any available' if selected resource was removed
     */
    const removeTypeWithAnyAvailableIfNecessary = useCallback(
      (type: string, resourceId: number) => {
        if (
          !anyAvailableResourceEnabled ||
          !typesWithAnyAvailableResource.includes(type)
        ) {
          return;
        }
        if (selectedResources[type]?.some((x) => x.id === resourceId)) {
          removeTypeWithAnyAvailableResource(type);
        }
      },
      [
        anyAvailableResourceEnabled,
        typesWithAnyAvailableResource,
        removeTypeWithAnyAvailableResource,
        selectedResources,
      ],
    );

    const onResourcePicked = useEventCallback(
      (resource: SchedulerResourceToPick) => {
        if (resourceTypeToAddFor) {
          if (
            resourceSettings[resourceTypeToAddFor]?.isMultiSelect ||
            selectedResources[resourceTypeToAddFor]?.some(
              (x) => x.id === resource.id,
            )
          ) {
            // if resource was picked manually, we should remove 'Any available' for this resource type
            removeTypeWithAnyAvailableResource(resourceTypeToAddFor);
          }

          if (isResourceAlreadyShown(resource, resourceTypeToAddFor)) {
            removeResource(resourceTypeToAddFor, resource.id);
            return;
          }
          pickResource(resourceTypeToAddFor, resource);
        }
      },
    );

    const ResourceTypeHeader = useCallback(
      (props: {
        type: string;
        isAnyAvailableEnabled: boolean;
        isExpanded: boolean;
      }) => {
        const ref: { current: HTMLAnchorElement | null } = { current: null };
        const { type, isAnyAvailableEnabled, isExpanded } = props;
        return (
          <div
            className={styles.resourceGroupHeader}
            data-test-id="resource-group-header"
          >
            {i18n.t(snakeCase(type) as any)}
            {type !== holidayPseudoResourceType && (
              <div className={styles.resourceAddContainer}>
                {isAnyAvailableEnabled && (
                  <span className={styles.anyAvailableResourceLabel}>
                    {i18n.t('any_available_resource_label')}
                  </span>
                )}
                <IconButton
                  color="blue"
                  ref={ref}
                  iconComponent={<AddContent />}
                  className={styles.resourceAddButton}
                  onClick={() => {
                    setResourceTypeToAddFor(type);
                    setResourceTypeToAdd_Element(ref.current);
                  }}
                  expanded={isExpanded}
                  tabIndex={0}
                  testId="open-resource-option-list"
                />
              </div>
            )}
          </div>
        );
      },
      [],
    );

    const isAllSelected = useMemo(() => {
      return (
        !resourceTypeToAddFor ||
        shownResources[resourceTypeToAddFor]?.filter((x) => !x.isDeleted)
          ?.length ===
          resourceGroups[resourceTypeToAddFor]?.filter((x) => !x.isDeleted)
            ?.length
      );
    }, [shownResources, resourceGroups, resourceTypeToAddFor]);

    const isNoneSelected = useMemo(() => {
      return (
        !resourceTypeToAddFor || !shownResources[resourceTypeToAddFor]?.length
      );
    }, [shownResources, resourceTypeToAddFor]);

    const renderOptionListHeader = useCallback(
      (type: string) => {
        return (
          <div
            className={styles.optionListHeader}
            onMouseDown={(e) => e.preventDefault()}
          >
            <span
              className={styles.optionListHeaderTitle}
              data-test-id="option-list-header-title"
            >
              {i18n.t(snakeCase(type) as any)}
            </span>
            <div>
              <a
                className={
                  isAllSelected
                    ? styles.optionListLinkDisabled
                    : styles.optionListLink
                }
                onClick={() => {
                  if (isAllSelected) return;
                  pickAllResources(type);
                  if (resourceSettings[type]?.isMultiSelect) {
                    removeTypeWithAnyAvailableResource(type);
                  }
                }}
              >
                {i18nFilter.t('checkbox_group_all')}
              </a>
              <a
                className={
                  isNoneSelected
                    ? styles.optionListLinkDisabled
                    : styles.optionListLink
                }
                onClick={() => {
                  if (isNoneSelected) return;
                  removeAllResources(type);
                  removeTypeWithAnyAvailableResource(type);
                }}
              >
                {i18nFilter.t('checkbox_group_none')}
              </a>
            </div>
          </div>
        );
      },
      [pickAllResources, removeAllResources, isAllSelected, isNoneSelected],
    );

    const handleAnyAvailableResourceClick = useCallback(
      (type: string) => {
        if (typesWithAnyAvailableResource.includes(type)) {
          removeTypeWithAnyAvailableResource(type);
        } else {
          addTypeWithAnyAvailableResource(type);
        }
        onPopupClose();
      },
      [
        typesWithAnyAvailableResource,
        addTypeWithAnyAvailableResource,
        removeTypeWithAnyAvailableResource,
        onPopupClose,
      ],
    );

    const renderAnyAvailableOption = useCallback(() => {
      return (
        <div className={styles.resourceItem}>
          <CheckBox
            name={`any-available-for-${resourceTypeToAddFor}`}
            checked={typesWithAnyAvailableResource.includes(
              resourceTypeToAddFor!,
            )}
            title={i18n.t('any_available_resource_option')}
          />
        </div>
      );
    }, [resourceTypeToAddFor, typesWithAnyAvailableResource]);

    const renderResourceOption = useCallback(
      (resource: SchedulerResourceToPick) => {
        return (
          <div
            className={styles.resourceItem}
            onClick={(event) => event.preventDefault()}
            data-test-id="resource-item"
          >
            <CheckBox
              title={resource.name}
              checked={(resourceTypeToAddFor
                ? shownResources[resourceTypeToAddFor]
                : []
              )
                .map((x) => x.id)
                .includes(resource.id)}
              name={resource.name}
            />
          </div>
        );
      },
      [shownResources, resourceTypeToAddFor],
    );
    return (
      <div className={styles.resourceSelector}>
        <section className={styles.resourceSelectorHeader} />
        {sortResourceKeys(resourceGroups).map((type) => {
          const resourceIsMultiselect = resourceSettings[type]?.isMultiSelect;
          const resourceIsEmbedded = resourceSettings[type]?.isEmbedded;
          const resourcesToShow = shownResources[type] || [];

          if (resourceIsEmbedded && resourcesToShow.length === 0) return null;

          const resourceList = (
            <div
              className={styles.resourceGroupList}
              data-test-id="resource-group-list"
            >
              {resourcesToShow.map((resource) => {
                const isResourceSelected = !!selectedResources[type]?.find(
                  (selectedResource) => selectedResource.id === resource.id,
                );
                return (
                  <div
                    key={resource.name}
                    className={clsx(
                      resourceIsEmbedded
                        ? styles.embeddedResource
                        : styles.resourceInSelection,
                      isResourceSelected && styles.selectedResourceCell,
                    )}
                  >
                    {resource.renderPrefix?.()}
                    {!resourceIsMultiselect && !resourceIsEmbedded ? (
                      <div
                        className={styles.resourceNameContainer}
                        data-test-id="resource-name"
                      >
                        <RadioButton
                          className={styles.resourceRadioButton}
                          name={type}
                          title={resource.name}
                          value={resource.id}
                          onChange={() => {
                            setResourceSelected(type, resource.id, true);
                            if (typesWithAnyAvailableResource.includes(type)) {
                              removeTypeWithAnyAvailableResource(type);
                            }
                          }}
                          checked={isResourceSelected}
                        />
                        {resource.isDeleted && (
                          <div className={styles.deletedResourceLabel}>
                            <span>{i18n.t('deleted_resource_label')}</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className={styles.resourceNameContainer}>
                        {resource.renderText ? (
                          resource.renderText()
                        ) : (
                          <span
                            className={styles.resourceName}
                            data-test-id="resource-name"
                          >
                            {resource.name}
                          </span>
                        )}
                        {resource.isDeleted && (
                          <span className={styles.deletedResourceLabel}>
                            {i18n.t('deleted_resource_label')}
                          </span>
                        )}
                      </div>
                    )}
                    {resource.renderPostfix?.()}
                    {!resourceIsEmbedded && (
                      <div>
                        <IconButton
                          color="grey"
                          iconComponent={<CrossIcon />}
                          className={styles.resourceRemoveButton}
                          onClick={() => {
                            removeTypeWithAnyAvailableIfNecessary(
                              type,
                              resource.id,
                            );
                            removeResource(type, resource.id);
                          }}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
          return (
            <div key={type} className={styles.resourceGroup}>
              {!resourceIsEmbedded ? (
                <ResourceTypeHeader
                  type={type}
                  isAnyAvailableEnabled={typesWithAnyAvailableResource.includes(
                    type,
                  )}
                  isExpanded={type === resourceTypeToAddFor}
                />
              ) : null}
              {resourceList}
            </div>
          );
        })}
        {resourceTypeToAddFor && (
          <OptionList
            onClose={onPopupClose}
            textClassName={styles.resourceText}
            optionContainerClassName={styles.resourceOption}
            isRecentlySearched={false}
            options={
              resourceGroups[resourceTypeToAddFor]
                ?.filter((x) => !x.isDeleted)
                .sort((a, b) =>
                  resourceSettings[resourceTypeToAddFor].isManualOrdering
                    ? a.order - b.order
                    : sortResourceValues(a.name, b.name),
                ) ?? []
            }
            onOptionClick={onResourcePicked}
            onAnyAvailableOptionClick={() =>
              handleAnyAvailableResourceClick(resourceTypeToAddFor)
            }
            extractOptionKey={extractResourceKey}
            renderHeader={() => renderOptionListHeader(resourceTypeToAddFor)}
            renderOption={renderResourceOption}
            renderAnyAvailableOption={
              anyAvailableResourceEnabled ? renderAnyAvailableOption : undefined
            }
            targetId={resourceTypeToAdd_Element}
          />
        )}
      </div>
    );
  },
);

function extractResourceKey(resource: SchedulerResourceToPick) {
  return resource.id;
}
